import React, { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { supabase } from '../../lib/supabase';
import { formatDistanceToNow } from '../../lib/dateUtils';

interface AuditEntry {
  id: string;
  table_name: string;
  action: string;
  created_at: string;
  old_values: any;
  new_values: any;
}

const AuditTrailWidget: React.FC = () => {
  const [recentChanges, setRecentChanges] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRecentChanges();
  }, []);

  const loadRecentChanges = async () => {
    try {
      const { data, error } = await supabase
        .from('audit_trail')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(8);

      if (error) throw error;
      setRecentChanges(data || []);
    } catch (err) {
      console.error('Error loading recent changes:', err);
      setError('Failed to load recent changes');
    } finally {
      setLoading(false);
    }
  };

  const getActionDescription = (entry: AuditEntry) => {
    const entityType = entry.table_name.replace(/_/g, ' ').toLowerCase();
    
    switch (entry.action) {
      case 'INSERT':
        return `Added new ${entityType}`;
      case 'UPDATE':
        return `Updated ${entityType}`;
      case 'DELETE':
        return `Deleted ${entityType}`;
      default:
        return `Modified ${entityType}`;
    }
  };

  if (error) {
    return (
      <Card className="h-96 flex flex-col">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <p className="text-red-600 text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-[28rem] flex flex-col">
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0 min-h-0">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900"></div>
          </div>
        ) : recentChanges.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500 text-center">No recent activity</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto relative scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div className="p-4 space-y-3">
            {recentChanges.map((entry) => (
              <div key={entry.id} className="flex items-start space-x-3 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                <div className="p-2 bg-blue-50 rounded-full">
                  <History className="h-4 w-4 text-blue-900" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {getActionDescription(entry)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(entry.created_at))} ago
                  </p>
                </div>
              </div>
            ))}
            </div>
            {/* Scroll indicator */}
            {recentChanges.length > 6 && (
              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none flex items-end justify-center pb-1">
                <div className="text-xs text-gray-600">Scroll for more</div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AuditTrailWidget;